// 消息服务：处理特殊消息的发送，包括metadata的构建
import { ChatMessage, MessageMetadata } from './aiChatService';

export interface SendMessageRequest {
    content: string;
    content_type: number; // 1=text, 5=voice, 6=image, 7=attachment, 9=location
    metadata: MessageMetadata;
    customerId: string;
}

export interface SendMessageResponse {
    success: boolean;
    messageId?: string;
    error?: string;
}

export interface FileUploadResponse {
    success: boolean;
    message: string;
    fileName: string;
    fileKey: string;
    fileUrl: string;
    fileSize: number;
    contentType: string;
    uploadTime: number;
    error: string | null;
}

// 后端返回的聊天消息结构
export interface ChatMessageRecord {
    id: number;
    userId: string;
    customerId: string;
    sender: number; // 1=user, 2=assistant
    content: string;
    contentType: number; // 1=text, 5=voice, 6=image, 7=attachment, 9=location
    messageData: any;
    parentMessageId: number | null;
    tokenCount: number | null;
    metadata: MessageMetadata | null;
    createdAt: string; // ISO时间字符串
    fromUser: boolean;
    fromAssistant: boolean;
    fromAgent: boolean;
    textMessage: boolean;
    voiceMessage: boolean;
    imageMessage: boolean;
    attachmentMessage: boolean;
}

// 分页信息
export interface PaginationInfo {
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    first: boolean;
    last: boolean;
    hasNext: boolean;
    hasPrevious: boolean;
}

// 获取对话记录的响应
export interface ChatMessageResponse {
    code: number;
    message: string;
    data?: ChatMessageRecord[];
    pagination?: PaginationInfo;
}

class MessageService {
    private readonly MESSAGE_API_URL = '/api/';

    /**
     * 构建语音消息的metadata
     */
    buildVoiceMetadata(uploadResponse: FileUploadResponse, transcription?: string, duration?: number): MessageMetadata {
        return {
            message_type: 'voice',
            voice_metadata: {
                file_id: uploadResponse.fileName,
                url: uploadResponse.fileUrl,
                duration: duration ? Math.round(duration / 1000) : undefined,
                format: this.getFormatFromContentType(uploadResponse.contentType),
                size: uploadResponse.fileSize,
                transcription: transcription ? {
                    text: transcription,
                    confidence: 0.95,
                    language: 'zh-CN',
                    provider: 'whisper'
                } : undefined
            }
        };
    }

    /**
     * 构建图片消息的metadata
     */
    buildImageMetadata(uploadResponse: FileUploadResponse, description?: string): MessageMetadata {
        return {
            message_type: 'image',
            image_metadata: {
                file_id: uploadResponse.fileName,
                url: uploadResponse.fileUrl,
                format: this.getFormatFromContentType(uploadResponse.contentType),
                size: uploadResponse.fileSize,
                description: description,
                alt_text: description || '用户发送的图片'
            }
        };
    }

    /**
     * 构建附件消息的metadata
     */
    buildAttachmentMetadata(uploadResponse: FileUploadResponse, description?: string): MessageMetadata {
        return {
            message_type: 'attachment',
            attachment_metadata: {
                file_id: uploadResponse.fileName,
                filename: uploadResponse.fileName,
                url: uploadResponse.fileUrl,
                mime_type: uploadResponse.contentType,
                size: uploadResponse.fileSize,
                description: description
            }
        };
    }

    /**
     * 构建位置消息的metadata
     */
    buildLocationMetadata(latitude: number, longitude: number, address?: string): MessageMetadata {
        return {
            message_type: 'location',
            location_metadata: {
                latitude,
                longitude,
                address,
                accuracy: 10
            }
        };
    }

    /**
     * 构建文本消息的metadata
     */
    buildTextMetadata(content: string): MessageMetadata {
        return {
            message_type: 'text',
            text_metadata: {
                length: content.length,
                language: 'zh-CN',
                encoding: 'utf-8'
            }
        };
    }

    /**
     * 发送消息到后端保存
     */
    async sendMessage(request: SendMessageRequest): Promise<SendMessageResponse> {
        try {
            const response = await fetch(`${this.MESSAGE_API_URL}/send`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(request),
                credentials: 'include'
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`发送消息失败: ${response.status} - ${errorText}`);
            }

            const result = await response.json();
            return {
                success: true,
                messageId: result.messageId
            };
        } catch (error) {
            console.error('发送消息失败:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : '发送消息失败'
            };
        }
    }

    /**
     * 从content-type获取文件格式
     */
    private getFormatFromContentType(contentType: string): string {
        const typeMap: { [key: string]: string } = {
            'image/jpeg': 'jpg',
            'image/jpg': 'jpg',
            'image/png': 'png',
            'image/gif': 'gif',
            'image/webp': 'webp',
            'audio/wav': 'wav',
            'audio/mp3': 'mp3',
            'audio/mpeg': 'mp3',
            'audio/webm': 'webm',
            'audio/ogg': 'ogg',
            'application/pdf': 'pdf',
            'application/msword': 'doc',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
            'application/vnd.ms-excel': 'xls',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx'
        };

        return typeMap[contentType.toLowerCase()] || contentType.split('/')[1] || 'unknown';
    }

    /**
     * 根据消息类型获取content_type
     */
    getContentType(messageType: string): number {
        const typeMap: { [key: string]: number } = {
            'text': 1,
            'voice': 5,
            'image': 6,
            'attachment': 7,
            'location': 9
        };
        return typeMap[messageType] || 1;
    }

    /**
     * 获取客户对话记录
     */
    async getCustomerConversations(
        customerId: string,
        page: number = 0,
        size: number = 50
    ): Promise<ChatMessageResponse> {
        try {
            const url = new URL(`${this.MESSAGE_API_URL}api/customers/${customerId}/conversations`, window.location.origin);
            url.searchParams.append('page', page.toString());
            url.searchParams.append('size', size.toString());

            console.log('获取客户对话记录:', url.toString());

            const response = await fetch(url.toString(), {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                },
                credentials: 'include'
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('获取对话记录失败:', response.status, errorText);
                throw new Error(`获取对话记录失败: ${response.status} - ${errorText}`);
            }

            const result: ChatMessageResponse = await response.json();
            console.log('对话记录获取成功:', result);
            return result;
        } catch (error) {
            console.error('获取对话记录异常:', error);
            return {
                code: 500,
                message: error instanceof Error ? error.message : '获取对话记录失败',
                data: [],
                pagination: {
                    page: 0,
                    size: 0,
                    totalElements: 0,
                    totalPages: 0,
                    first: true,
                    last: true,
                    hasNext: false,
                    hasPrevious: false
                }
            };
        }
    }
}

export const messageService = new MessageService();
export default MessageService;
